/**
 * @fileoverview Render API Contract
 * @module contracts/render
 *
 * Defines the unified data contract for the rendering service.
 * Used by Backend, Render Service, and Template Engine.
 */

import { z } from 'zod';
import { createContract } from './contract.utils';
import { PdfTypeSchema, PdfType } from './pdf.contract';
import { StructuredPdfContentSchema } from './pdf-content.contract';
import type { StructuredPdfContent } from './pdf-content.contract';

// ============================================================================
// DATA SCHEMAS
// ============================================================================

/**
 * Learning Style Data Schema
 */
export const LearningStyleSchema = z.object({
	title: z.string(),
	score: z.number().min(0).max(100),
	icon: z.string(),
});
// Explicit interface to prevent Vue compiler issues with complex Zod inference
export interface LearningStyle {
	title: string;
	score: number;
	icon: string;
}

/**
 * Screening Result Data Schema
 */
export const ScreeningResultSchema = z.object({
	title: z.string(),
	score: z.number().min(0).max(100),
	icon: z.string(),
});
// Explicit interface
export interface ScreeningResult {
	title: string;
	score: number;
	icon: string;
}

/**
 * Overview Data Schema (Charts & Summaries)
 */
export const PdfOverviewDataSchema = z.object({
	learningStyles: z.array(LearningStyleSchema),
	screeningResults: z.array(ScreeningResultSchema),
	totalQuestions: z.number().int().nonnegative(),
});
// Explicit interface
export interface PdfOverviewData {
	learningStyles: LearningStyle[];
	screeningResults: ScreeningResult[];
	totalQuestions: number;
}

/**
 * Analysis Section Schema
 * Handles both plain text and structured blocks
 */
export const AnalysisSectionSchema = z.object({
	title: z.string(),
	content: z.union([
		StructuredPdfContentSchema, // JSON blocks from AI
		z.object({ text: z.string(), isPlainText: z.literal(true) }), // Legacy/Fallback plain text
	]),
	isPlainText: z.boolean(),
});
// Explicit interface
export interface AnalysisSection {
	title: string;
	content: StructuredPdfContent | { text: string; isPlainText: true };
	isPlainText: boolean;
}

/**
 * Complete PDF Render Data Schema
 * This is the full payload sent to the template engine
 */
export const PdfRenderDataSchema = z.object({
	childName: z.string(),
	childAge: z.number().int().nonnegative(),
	generatedDate: z.string().datetime(),
	testId: z.string(),
	pdfType: PdfTypeSchema,
	radarChartData: z.string().optional(), // Base64 image
	barChartData: z.string().optional(), // Base64 image
	overviewData: PdfOverviewDataSchema,
	analysisContent: z.array(AnalysisSectionSchema),
});
// Explicit interface
export interface PdfRenderData {
	childName: string;
	childAge: number;
	generatedDate: string;
	testId: string;
	pdfType: PdfType;
	radarChartData?: string;
	barChartData?: string;
	overviewData: PdfOverviewData;
	analysisContent: AnalysisSection[];
}

/**
 * Render Request Schema
 */
export const RenderRequestSchema = z.object({
	template: z.string(),
	data: PdfRenderDataSchema.or(z.record(z.string(), z.unknown())),
	format: z.enum(['pdf', 'html', 'png']).default('pdf'),
	options: z
		.object({
			pdf: z
				.object({
					margin: z.unknown().optional(),
				})
				.optional(),
			viewport: z.unknown().optional(),
			// Footer configuration for Puppeteer native footer
			footer: z
				.object({
					enabled: z.boolean().default(true), // Enable native footer by default
					template: z.string().optional(), // Custom footer HTML template
					// Data to inject into footer template
					data: z
						.object({
							testId: z.string().optional(),
							generatedDate: z.string().optional(),
							brandingText: z.string().optional(),
						})
						.optional(),
				})
				.optional(),
		})
		.optional(),
});
// Explicit interface
export interface RenderRequest {
	template: string;
	data: PdfRenderData | Record<string, unknown>;
	format: 'pdf' | 'html' | 'png';
	options?: {
		pdf?: {
			margin?: unknown;
		};
		viewport?: unknown;
		footer?: {
			enabled?: boolean;
			template?: string;
			data?: {
				testId?: string;
				generatedDate?: string;
				brandingText?: string;
			};
		};
	};
}

// Type assertions removed - explicit interfaces provide the same safety
// The Zod schemas and interfaces are kept in sync manually

// ============================================================================
// API CONTRACT
// ============================================================================

export const RENDER_API_CONTRACT = createContract('/render', {
	/**
	 * POST /render
	 * Render a template to PDF/HTML/PNG
	 */
	render: {
		method: 'POST' as const,
		path: '/',
		body: RenderRequestSchema,
		response: z.any(), // Binary response
	},

	/**
	 * GET /render/health
	 * Health check
	 */
	health: {
		method: 'GET' as const,
		path: '/health',
		response: z.object({ status: z.literal('ok') }),
	},
});
